import { useState } from "react";
import { HeartHandshake, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";

const PACKS = [49, 99, 499, 999];

const loadRzp = () =>
  new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true);
    s.onerror = () => res(false);
    document.body.appendChild(s);
  });

export default function SupportUs() {
  const { lang } = useLang();
  const [busy, setBusy] = useState(0);
  const [done, setDone] = useState(0);

  const pay = async (amount) => {
    setBusy(amount);
    try {
      const { data: cfg } = await api.get("/payments/razorpay/config");
      if (!cfg.enabled) {
        toast.info(
          lang === "hi"
            ? "पेमेंट अभी सेटअप में हैं — बहुत जल्द चालू हो जाएंगे"
            : "Payments are being set up — live very soon"
        );
        setBusy(0);
        return;
      }
      await loadRzp();
      const { data: order } = await api.post("/payments/razorpay/order", { amount });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: "INR",
        name: "The Vainor India",
        description: lang === "hi" ? "पाठक सहयोग" : "Reader Support",
        order_id: order.order_id,
        theme: { color: "#0F382C" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", resp);
            setDone(amount);
            toast.success(lang === "hi" ? "धन्यवाद! आपका सहयोग मिल गया" : "Thank you for your support!");
          } catch {
            toast.error(lang === "hi" ? "पेमेंट verify नहीं हो पाया — संपर्क करें" : "Verification failed — contact us");
          }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(0);
  };

  if (done) {
    return (
      <section data-testid="support-us" className="bg-deepgreen text-paper border-y-4 border-gold p-8 md:p-12 text-center">
        <HeartHandshake size={40} className="mx-auto text-gold" />
        <h2 className="font-hindi text-2xl md:text-4xl text-gold-light mt-4">
          {lang === "hi" ? "धन्यवाद! आपके ₹" + done + " मिल गए" : `Thank you! Your ₹${done} was received`}
        </h2>
        <p className="text-paper/75 text-sm md:text-base mt-3 max-w-xl mx-auto leading-relaxed">
          {lang === "hi"
            ? "आपका सहयोग निष्पक्ष और निर्भीक पत्रकारिता को मजबूत करता है। — The Vainor Ventures"
            : "Your contribution keeps independent, fearless journalism alive. — The Vainor Ventures"}
        </p>
      </section>
    );
  }

  return (
    <section data-testid="support-us" className="bg-deepgreen text-paper border-y-4 border-gold p-8 md:p-12">
      <div className="max-w-3xl mx-auto text-center">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
          {lang === "hi" ? "स्वतंत्र पत्रकारिता को सहयोग दें" : "Support independent journalism"}
        </span>
        <h2 className="font-hindi text-2xl md:text-4xl text-gold-light mt-3">
          {lang === "hi" ? "The Vainor India को सपोर्ट करें" : "Support The Vainor India"}
        </h2>
        <p className="text-paper/75 text-sm md:text-base mt-3 leading-relaxed">
          {lang === "hi"
            ? "हमारी हर ख़बर मुफ़्त है — क्योंकि सत्य की कीमत नहीं होती। अगर हमारा काम पसंद आए, तो छोटा-सा सहयोग हमें और मजबूत बनाएगा।"
            : "Every story we publish is free — because truth has no price. If you value our work, a small contribution makes us stronger."}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
          {PACKS.map((amt) => (
            <button
              key={amt}
              data-testid={`support-${amt}`}
              onClick={() => pay(amt)}
              disabled={!!busy}
              className="border-2 border-gold text-gold-light font-hindi text-2xl py-4 hover:bg-gold hover:text-deepgreen transition-colors disabled:opacity-60"
            >
              {busy === amt ? "…" : `₹${amt}`}
            </button>
          ))}
        </div>
        <p className="flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50 mt-5">
          <ShieldCheck size={13} /> UPI • Cards • NetBanking — Razorpay
        </p>
      </div>
    </section>
  );
}
